import { Button, Stack, Text } from "flicket-ui";
import { SocialShareFooter } from "~components/common/Social/SocialShareFooter";
import CustomModal from "~components/CustomModal";
import { CompetitionProvider } from "~features/competitions/CompetitionContext";
import { createPromoterTitles } from "~features/competitions/helpers";
import { CompetitionType } from "~graphql/typed-document-nodes";
import { useOrganization } from "~hooks";
import { useActiveCompetitionByType } from "../hooks/useActiveCompetitions";
import { PageContext } from "../hooks/useDetailsPageContext";
import { PromoterShareCard } from "./PromoterShareCard";

interface RegistrationSuccessModalContentProps {
  pageContext: PageContext;
}

export const RegistrationSuccessModalContent = ({
  pageContext,
}: RegistrationSuccessModalContentProps) => {
  const {
    event: { presaleRelease },
  } = pageContext;

  const { hasFeature } = useOrganization();

  const isPrePurchaseActive =
    presaleRelease?.isPrePurchaseActive &&
    hasFeature("prePurchaseRegistration");

  return isPrePurchaseActive ? (
    <RegistrationSuccessWithPrePurchaseCard pageContext={pageContext} />
  ) : (
    <RegistrationSuccessWithShareCard pageContext={pageContext} />
  );
};

const RegistrationSuccessWithShareCard = ({
  pageContext,
}: RegistrationSuccessModalContentProps) => {
  const {
    waitlistUserId,
    event: { id: eventId, title: eventTitle },
    userEmail,
  } = pageContext;

  const { activeCompetition } = useActiveCompetitionByType(
    CompetitionType.Registration,
    eventId
  );

  const { title, subtitle } = createPromoterTitles(
    waitlistUserId && activeCompetition ? activeCompetition : undefined
  );

  return (
    <CompetitionProvider
      userId={waitlistUserId}
      competition={activeCompetition}
    >
      <CustomModal.Header>You&lsquo;re on the list 🎉</CustomModal.Header>
      <CustomModal.Content>
        <Stack direction={"vertical"} gap={4}>
          <Text variant="regular">
            We&lsquo;ll email you at {userEmail} as soon as presale tickets go
            on sale.
          </Text>
          <Stack direction={"vertical"}>
            <Text variant="header.S">{title}</Text>
            {subtitle && <Text variant="regular">{subtitle}</Text>}
          </Stack>
          <PromoterShareCard
            eventId={eventId}
            eventTitle={eventTitle}
            waitlistUserId={waitlistUserId}
            eventPhase={CompetitionType.Registration}
          />
        </Stack>
      </CustomModal.Content>
      {!!activeCompetition && <SocialShareFooter />}
    </CompetitionProvider>
  );
};

const RegistrationSuccessWithPrePurchaseCard = ({
  pageContext,
}: RegistrationSuccessModalContentProps) => {
  const presaleLink = `/events/${pageContext.event.id}/reservation?release=${pageContext.event.presaleRelease?.id}`;

  return (
    <>
      <CustomModal.Header>You&lsquo;re on the list 🎉</CustomModal.Header>
      <CustomModal.Content>
        <Stack direction={"vertical"} gap={4}>
          <Text variant="regular">
            We&lsquo;ll email you at {pageContext.userEmail} as soon as presale
            tickets go on sale.
          </Text>
          <Stack
            direction="vertical"
            backgroundColor="rgba(0, 0, 0, 0.03)"
            borderRadius="md"
            padding={4}
            gap={3}
            textAlign="center"
          >
            <Text variant="header.M">
              Beat the crowd. Secure your tickets early.
            </Text>
            <Stack direction="vertical" gap={2}>
              <Button variant="primary" href={presaleLink}>
                Secure best price tickets
              </Button>
              <Text variant="regular">
                Secure early access at today&apos;s price. No payment today, and
                you can cancel anytime before the charge.
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </CustomModal.Content>
    </>
  );
};
